import { useState } from "react";
import { Flag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { id: "scary", label: "It felt scary or upsetting" },
  { id: "not_for_kids", label: "It doesn't seem right for kids" },
  { id: "looks_wrong", label: "Something looks wrong or untrue" },
  { id: "other", label: "Something else" },
];

// The mechanism behind the guidelines' promise: "tell us and we'll fix the
// rule, not just remove the story." Anonymous by design — no account, no
// email, nothing about the reader is sent or stored; just the story + reason.
export const StoryReport = ({ article }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(null);
  const [comment, setComment] = useState("");
  const [state, setState] = useState("idle"); // idle | sending | done | error

  const submit = async () => {
    if (!reason || state === "sending") return;
    setState("sending");
    try {
      const res = await fetch(`${API}/feedback/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          article_id: article?.id || null,
          reason,
          comment: comment.slice(0, 500),
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setState("done");
    } catch {
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <p className="mt-6 pt-4 border-t border-scoop-border text-[0.8rem] text-scoop-sage font-semibold" data-testid="report-thanks">
        💚 Thanks for telling us — a grown-up on our team will take a look.
      </p>
    );
  }

  return (
    <div className="mt-6 pt-4 border-t border-scoop-border" onClick={(e) => e.stopPropagation()}>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-[0.75rem] font-semibold text-scoop-muted hover:text-scoop-blue transition-colors"
          data-testid="report-story-btn"
        >
          <Flag className="w-3.5 h-3.5" />
          Something not right with this story? Tell us
        </button>
      ) : (
        <div className="space-y-3" data-testid="report-story-form">
          <p className="text-[0.8rem] font-bold text-scoop-ink">
            What's wrong? (Your report is anonymous.)
          </p>
          <div className="flex flex-wrap gap-2">
            {REASONS.map((r) => (
              <button
                key={r.id}
                onClick={() => setReason(r.id)}
                className={`text-[0.75rem] font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                  reason === r.id
                    ? "bg-scoop-blue text-white border-scoop-blue"
                    : "bg-white text-scoop-muted border-scoop-border hover:border-scoop-blue"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="Want to add anything? (optional)"
            className="w-full text-[0.8rem] border border-scoop-border rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-scoop-blue/40"
          />
          <div className="flex items-center gap-3">
            <button
              onClick={submit}
              disabled={!reason || state === "sending"}
              className="bg-scoop-blue text-white text-[0.75rem] font-bold px-5 py-2 rounded-full hover:bg-scoop-blue/90 disabled:opacity-50"
              data-testid="report-submit-btn"
            >
              {state === "sending" ? "Sending…" : "Send to our team"}
            </button>
            <button
              onClick={() => setOpen(false)}
              className="text-[0.75rem] font-semibold text-scoop-muted hover:text-scoop-ink"
            >
              Never mind
            </button>
            {state === "error" && (
              <span className="text-[0.75rem] text-red-600">Couldn't send — try again?</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StoryReport;
